/**
 * FartTypes - Definitions for all fart types in the game
 * 
 * Each fart type has unique behavior, visuals, and stats.
 */

/**
 * Enum of all available fart types
 */
export enum FartType {
    BASIC = 'basic',
    LINGERING = 'lingering',
    GHOST = 'ghost',
    EXPLOSIVE = 'explosive',
    SBD = 'sbd',           // Silent But Deadly
    PROPULSION = 'propulsion',
}

/**
 * Pickup types that grant fart ammo
 */
export enum PickupType {
    BEANS = 'beans',           // → Lingering
    MYSTERY_MEAT = 'mystery_meat', // → Ghost
    HOT_PEPPER = 'hot_pepper',     // → Explosive
    EGG = 'egg',               // → SBD
    FIBER_SALAD = 'fiber_salad',   // → Propulsion
}

/**
 * Configuration for each fart type
 */
export interface FartTypeConfig {
    type: FartType;
    name: string;
    description: string;

    // Visual
    color: number;          // Primary tint color
    icon: string;           // Emoji for UI

    // Stats (base values, may scale with charge for some types)
    damage: number;
    stunDuration: number;   // ms
    duration: number;       // How long the fart lasts (ms)

    // Ammo
    usesPerPickup: number;
    isUnlimited: boolean;   // Basic fart uses gas capacity instead of ammo

    // Behavior flags
    canPierceWalls: boolean;
    canSelfDamage: boolean;
    hasMovement: boolean;   // Does it move on its own?

    // Pickup association
    pickup?: PickupType;
    spawnWeight: number;    // Relative spawn chance (0-100)
}

/**
 * All fart type configurations
 */
export const FART_CONFIGS: Record<FartType, FartTypeConfig> = {
    [FartType.BASIC]: {
        type: FartType.BASIC,
        name: 'Basic Fart',
        description: 'The classic. Always available.',
        color: 0x7bed9f,        // Green
        icon: '💨',
        damage: 5,              // Base, scales with charge (5-50)
        stunDuration: 250,      // Base, scales with charge (250-1500)
        duration: 600,
        usesPerPickup: 0,
        isUnlimited: true,
        canPierceWalls: false,  // Only at 50%+ charge
        canSelfDamage: false,
        hasMovement: false,
        spawnWeight: 0,         // Not spawned as pickup
    },

    [FartType.LINGERING]: {
        type: FartType.LINGERING,
        name: 'Lingering Fart',
        description: 'Hangs in the air. Perfect for blocking passages.',
        color: 0xc8d6a0,        // Yellow-green
        icon: '☁️',
        damage: 8,              // Per tick (every 0.5s)
        stunDuration: 300,      // Per tick
        duration: 5000,         // 5 seconds
        usesPerPickup: 2,
        isUnlimited: false,
        canPierceWalls: false,
        canSelfDamage: false,
        hasMovement: false,
        pickup: PickupType.BEANS,
        spawnWeight: 30,
    },

    [FartType.GHOST]: {
        type: FartType.GHOST,
        name: 'Ghost Fart',
        description: 'Has a mind of its own. Wanders the maze seeking victims.',
        color: 0xdfe6e9,        // Pale purple-white
        icon: '👻',
        damage: 25,
        stunDuration: 1000,
        duration: 9000,         // 8-10 seconds
        usesPerPickup: 1,
        isUnlimited: false,
        canPierceWalls: true,   // Confirmed: can phase through walls
        canSelfDamage: false,   // Ignores owner for 2s, then can hit anyone
        hasMovement: true,
        pickup: PickupType.MYSTERY_MEAT,
        spawnWeight: 15,
    },

    [FartType.EXPLOSIVE]: {
        type: FartType.EXPLOSIVE,
        name: 'Explosive Fart',
        description: 'One massive blast. High risk, high reward.',
        color: 0xff6b6b,        // Orange-red
        icon: '💥',
        damage: 60,
        stunDuration: 2000,
        duration: 800,          // 0.8s delay + instant expansion
        usesPerPickup: 1,
        isUnlimited: false,
        canPierceWalls: true,   // Shockwave goes through walls
        canSelfDamage: true,    // Confirmed: can hurt self
        hasMovement: false,
        pickup: PickupType.HOT_PEPPER,
        spawnWeight: 15,
    },

    [FartType.SBD]: {
        type: FartType.SBD,
        name: 'Silent But Deadly',
        description: 'Nearly invisible. They won\'t know what hit them.',
        color: 0xfdfefe,        // Nearly white/clear
        icon: '🎯',
        damage: 35,
        stunDuration: 1500,
        duration: 3000,         // Travels until hitting something
        usesPerPickup: 3,
        isUnlimited: false,
        canPierceWalls: false,
        canSelfDamage: false,
        hasMovement: true,      // Travels in straight line
        pickup: PickupType.EGG,
        spawnWeight: 25,
    },

    [FartType.PROPULSION]: {
        type: FartType.PROPULSION,
        name: 'Propulsion Fart',
        description: 'Not for damage - for SPEED!',
        color: 0x8b4513,        // Brown
        icon: '🌀',
        damage: 5,              // Minimal damage
        stunDuration: 0,
        duration: 300,          // Brief visual
        usesPerPickup: 4,
        isUnlimited: false,
        canPierceWalls: false,
        canSelfDamage: false,
        hasMovement: false,     // Player moves, not the fart
        pickup: PickupType.FIBER_SALAD,
        spawnWeight: 15,
    },
};

/**
 * Pickup configurations
 */
export interface PickupConfig {
    type: PickupType;
    name: string;
    icon: string;
    grantsFart: FartType;
    color: number;          // Glow color
}

export const PICKUP_CONFIGS: Record<PickupType, PickupConfig> = {
    [PickupType.BEANS]: {
        type: PickupType.BEANS,
        name: 'Can of Beans',
        icon: '🫘',
        grantsFart: FartType.LINGERING,
        color: 0xc8d6a0,
    },
    [PickupType.MYSTERY_MEAT]: {
        type: PickupType.MYSTERY_MEAT,
        name: 'Mystery Meat',
        icon: '🥩',
        grantsFart: FartType.GHOST,
        color: 0xdfe6e9,
    },
    [PickupType.HOT_PEPPER]: {
        type: PickupType.HOT_PEPPER,
        name: 'Hot Pepper',
        icon: '🌶️',
        grantsFart: FartType.EXPLOSIVE,
        color: 0xff6b6b,
    },
    [PickupType.EGG]: {
        type: PickupType.EGG,
        name: 'Hard-Boiled Egg',
        icon: '🥚',
        grantsFart: FartType.SBD,
        color: 0xfdfefe,
    },
    [PickupType.FIBER_SALAD]: {
        type: PickupType.FIBER_SALAD,
        name: 'Fiber Salad',
        icon: '🥗',
        grantsFart: FartType.PROPULSION,
        color: 0x27ae60,
    },
};

/**
 * Propulsion-specific constants
 */
export const PROPULSION_CONFIG = {
    dashDuration: 300,          // ms
    dashSpeed: 600,             // pixels per second
    invincibilityDuration: 1000, // 1 second (confirmed)
    cooldown: 500,              // ms between uses
};

/**
 * Ghost fart AI constants
 */
export const GHOST_CONFIG = {
    speed: 120,                 // 60% of player speed (200 * 0.6)
    detectionRadius: 96,        // 3 tiles (32 * 3)
    ownerIgnoreDuration: 2000,  // 2 seconds before it can hit owner
};

/**
 * Lingering fart constants
 */
export const LINGERING_CONFIG = {
    tickInterval: 500,          // Damage every 0.5s
    pulseMin: 0.9,              // Scale pulse range
    pulseMax: 1.1,
    pulseDuration: 800,         // ms per pulse cycle
};

/**
 * Explosive fart constants
 */
export const EXPLOSIVE_CONFIG = {
    chargeDelay: 1500,          // 1.5s warning before explosion
    knockbackForce: 300,        // Push force on nearby players
    blastRadius: 102,           // ~3 tiles (reduced 20% from 128)
};

/**
 * SBD fart constants
 */
export const SBD_CONFIG = {
    speed: 300,                 // 150% of player speed
    opacity: 0.05,              // 95% transparent
    shimmerFrequency: 100,      // Heat shimmer flicker rate
};

/**
 * Helper to get fart config by type
 */
export function getFartConfig(type: FartType): FartTypeConfig {
    return FART_CONFIGS[type];
}

/**
 * Helper to get pickup config by type
 */
export function getPickupConfig(type: PickupType): PickupConfig {
    return PICKUP_CONFIGS[type];
}

/**
 * Get fart type from pickup type
 */
export function getFartTypeFromPickup(pickup: PickupType): FartType {
    return PICKUP_CONFIGS[pickup].grantsFart;
}

/**
 * Get all special fart types (excluding Basic)
 */
export function getSpecialFartTypes(): FartType[] {
    return Object.values(FartType).filter(t => t !== FartType.BASIC);
}

/**
 * Get pickup spawn weights as normalized probabilities
 */
export function getPickupSpawnProbabilities(): Record<PickupType, number> {
    const probs: Record<string, number> = {};
    let totalWeight = 0;

    for (const config of Object.values(FART_CONFIGS)) {
        if (config.pickup) {
            totalWeight += config.spawnWeight;
        }
    }

    for (const config of Object.values(FART_CONFIGS)) {
        if (config.pickup) {
            probs[config.pickup] = config.spawnWeight / totalWeight;
        }
    }

    return probs as Record<PickupType, number>;
}

/**
 * Get ammo amount for a pickup type
 */
export function getPickupAmmoAmount(pickupType: PickupType): number {
    const fartType = getFartTypeFromPickup(pickupType);
    return FART_CONFIGS[fartType].usesPerPickup;
}
